/**
 * VNC Pod 管理与聊天同步 API
 *
 * 功能包括：
 * 1. VNC Pod 生命周期管理（创建、查询、重启、停止、删除）
 * 2. 用户聊天记录同步到 VNC Pod
 */
import axios from 'axios'
import http from './httpRequest.js'

console.log('[VNC API] 🚀 vncApi.js 模块已加载')
console.log('[VNC API] baseURL配置:', process.env.VUE_APP_VNC_API_BASE_URL)

// 创建专用的VNC API axios实例
const vncHttp = axios.create({
	baseURL: process.env.VUE_APP_VNC_API_BASE_URL || 'https://api.vnc.service.thinkgs.cn',
	timeout: 1000 * 60 * 2, // VNC Pod创建可能需要较长时间，设置2分钟超时
	withCredentials: false
})

console.log('[VNC API] vncHttp实例已创建，baseURL:', vncHttp.defaults.baseURL)

// 添加请求拦截器，携带认证token
vncHttp.interceptors.request.use(config => {
	const tokenKey = sessionStorage.getItem("tokenKey");
	console.log('[VNC API] 📤 发送请求:', {
		url: config.baseURL + config.url,
		method: config.method,
		hasToken: !!tokenKey
	});
	if (tokenKey) {
		config.headers.Authorization = `Bearer ${tokenKey}`;
	}
	return config
}, error => {
	console.error('[VNC API] ❌ 请求拦截器错误:', error);
	return Promise.reject(error)
})

// 添加响应拦截器
vncHttp.interceptors.response.use(response => {
	console.log('[VNC API] ✅ 收到响应:', {
		url: response.config.url,
		status: response.status,
		data: response.data
	});
	// VNC API直接返回数据，不需要额外处理
	return response.data;
}, error => {
	console.error('[VNC API] ❌ 请求失败:', error);
	console.error('[VNC API] ❌ 错误详情:', {
		url: error.config?.url,
		status: error.response?.status,
		message: error.message
	});
	if (error.response) {
		// 服务器返回了错误响应
		const message = error.response.data?.detail || error.response.data?.message || error.message;
		return Promise.reject(new Error(message));
	} else if (error.request) {
		// 请求已发出，但没有收到响应
		return Promise.reject(new Error('VNC API服务无响应，请检查网络连接'));
	} else {
		// 其他错误
		return Promise.reject(error);
	}
})

// =============================================================================
// VNC Pod 管理 API
// =============================================================================

/**
 * 列出用户的所有 VNC Pod
 * @returns {Promise} Pod列表信息
 */
export function listPods() {
	return vncHttp({
		url: '/api/v1/pods',
		method: 'GET'
	})
}

/**
 * 获取指定 Pod 的详细信息
 * @param {string} podName - Pod名称
 * @returns {Promise} Pod详细信息
 */
export function getPodDetail(podName) {
	return vncHttp({
		url: `/api/v1/pods/${podName}`,
		method: 'GET'
	})
}

/**
 * 创建新的 VNC Pod
 * @param {Object} config - Pod配置参数
 * @param {string} config.cpu_limit - CPU限制，默认 '2'
 * @param {string} config.memory_limit - 内存限制，默认 '4Gi'
 * @param {string} config.storage_size - 存储大小，默认 '20Gi'
 * @returns {Promise} 创建结果，包含pod_name、vnc_password、access_info等
 */
export function createPod(config = {}) {
	const defaultConfig = {
		cpu_limit: '2',
		memory_limit: '4Gi',
		storage_size: '20Gi'
	}

	return vncHttp({
		url: '/api/v1/pods',
		method: 'POST',
		data: { ...defaultConfig, ...config }
	})
}

/**
 * 重启指定的 Pod
 * @param {string} podName - Pod名称
 * @returns {Promise} 操作结果
 */
export function restartPod(podName) {
	return vncHttp({
		url: `/api/v1/pods/${podName}/restart`,
		method: 'POST'
	})
}

/**
 * 停止指定的 Pod（保留PVC）
 * @param {string} podName - Pod名称
 * @returns {Promise} 操作结果
 */
export function stopPod(podName) {
	return vncHttp({
		url: `/api/v1/pods/${podName}/stop`,
		method: 'POST'
	})
}

/**
 * 删除指定的 Pod（删除PVC，数据永久丢失）
 * @param {string} podName - Pod名称
 * @returns {Promise} 操作结果
 */
export function deletePod(podName) {
	return vncHttp({
		url: `/api/v1/pods/${podName}`,
		method: 'DELETE'
	})
}

/**
 * 检查用户的 VNC Pod 状态
 * @returns {Promise<Object>} Pod状态信息
 * @returns {boolean} exists - Pod是否存在
 * @returns {string} status - Pod状态 ('Running', 'Pending', 'Failed', 'Stopped')
 * @returns {Object} podInfo - Pod详细信息（如果存在）
 */
export async function checkPodStatus() {
	console.log('[VNC API] 🔍 开始检查Pod状态...')
	try {
		const result = await listPods()
		console.log('[VNC API] listPods 结果:', result)

		if (!result || !result.pods || result.pods.length === 0) {
			return {
				exists: false,
				status: null,
				podInfo: null
			}
		}

		const pod = result.pods[0]
		const podName = pod.name

		// 获取Pod详细信息
		const podDetail = await getPodDetail(podName)

		return {
			exists: true,
			status: podDetail.phase || podDetail.status || pod.status,
			podInfo: podDetail,
			podName: podName
		}
	} catch (error) {
		console.error('[VNC API] 检查Pod状态失败:', error)
		return {
			exists: false,
			status: null,
			podInfo: null,
			error: error.message
		}
	}
}

/**
 * 确保 VNC Pod 已创建并正在运行
 * 如果Pod不存在，会自动创建；如果Pod存在但未运行，会等待其就绪
 *
 * @param {Object} options - 配置选项
 * @param {Function} options.onCreating - Pod创建中的回调函数
 * @param {Function} options.onWaiting - Pod等待就绪的回调函数
 * @param {number} options.maxWaitTime - 最大等待时间（毫秒），默认120秒
 * @returns {Promise<Object>} Pod信息
 */
export async function ensurePodReady(options = {}) {
	console.log('[VNC API] 🎯 ensurePodReady 本地mock，直接返回就绪')
	return { ready: true, podInfo: {}, podName: 'local' }
}

// =============================================================================
// 聊天同步 API
// =============================================================================

/**
 * 获取用户的所有聊天列表（私聊和群聊）
 * @param {number} userId - 用户ID
 * @returns {Promise} 包含私聊ID列表和群聊ID列表的对象
 */
export async function getUserChats(userId) {
	console.log(`[VNC Sync] 📤 请求用户聊天列表: /user/${userId}/chats`)
	try {
		const result = await http({
			url: `/user/${userId}/chats`,
			method: 'GET'
		})
		console.log('[VNC Sync] ✅ 获取聊天列表成功:', result)
		return result
	} catch (error) {
		console.error('[VNC Sync] ❌ 获取聊天列表失败:', error)
		throw error
	}
}

/**
 * 同步用户的聊天符号链接到 VNC Pod
 * @param {Object} params - 同步参数
 * @param {number} params.user_id - 用户ID
 * @param {Array<string>} params.private_chat_ids - 私聊会话ID列表（格式：{minUid}-{maxUid}）
 * @param {Array<number>} params.group_ids - 群聊ID列表
 * @returns {Promise} 同步结果，包含成功数量和错误信息
 */
export function syncUserChats(params) {
	return vncHttp({
		url: '/api/v1/chats/sync',
		method: 'POST',
		data: params
	})
}

/**
 * 获取用户聊天符号链接状态
 * @param {number} userId - 用户ID
 * @returns {Promise} 符号链接状态信息
 */
export function getUserChatsStatus(userId) {
	return vncHttp({
		url: `/api/v1/chats/${userId}/status`,
		method: 'GET'
	})
}

/**
 * 自动同步用户所有聊天到 VNC Pod
 * 该函数会先获取用户的所有聊天，然后调用同步接口
 * @param {number} userId - 用户ID
 * @returns {Promise} 同步结果
 */
export async function autoSyncUserChats(userId) {
	try {
		console.log(`[VNC Sync] 开始同步用户 ${userId} 的聊天...`)

		// 1. 获取用户的所有聊天列表
		let chatsData = null;
		try {
			chatsData = await getUserChats(userId);
			console.log('[VNC Sync] getUserChats 返回数据:', chatsData);
		} catch (error) {
			console.error('[VNC Sync] ⚠️ getUserChats 失败，但继续执行:', error);
			// 降级：如果获取聊天列表失败，不阻止 Pod 初始化
			// 返回成功但同步数为0，让用户可以继续使用智能体
			return {
				success: true,
				message: '聊天列表获取失败，已跳过同步',
				private_chats_synced: 0,
				groups_synced: 0,
				errors: [error.message || '获取聊天列表失败']
			};
		}

		// httpRequest.js 已经提取了 data 字段，所以 chatsData 直接就是数据对象
		if (!chatsData) {
			console.warn('[VNC Sync] 未获取到聊天数据，chatsData:', chatsData)
			return {
				success: false,
				message: '未获取到聊天数据'
			}
		}

		const { privateChatIds = [], groupIds = [] } = chatsData

		console.log(`[VNC Sync] 获取到 ${privateChatIds.length} 个私聊, ${groupIds.length} 个群聊`)
		console.log('[VNC Sync] privateChatIds:', privateChatIds)
		console.log('[VNC Sync] groupIds:', groupIds)

		// 2. 如果没有聊天，直接返回
		if (privateChatIds.length === 0 && groupIds.length === 0) {
			console.log('[VNC Sync] 用户暂无聊天，跳过同步')
			return {
				success: true,
				message: '用户暂无聊天',
				private_chats_synced: 0,
				groups_synced: 0,
				errors: []
			}
		}

		// 3. 调用同步接口
		console.log('[VNC Sync] 📤 开始调用同步接口...')
		const syncResult = await syncUserChats({
			user_id: userId,
			private_chat_ids: privateChatIds,
			group_ids: groupIds
		})

		console.log('[VNC Sync] ✅ 同步完成:', syncResult)

		return syncResult

	} catch (error) {
		console.error('[VNC Sync] ❌ 同步失败:', error)
		// 🔥 降级：同步失败不阻止智能体初始化
		// 返回部分成功，让用户可以继续使用
		return {
			success: true,
			message: '聊天同步失败，但可以继续使用',
			private_chats_synced: 0,
			groups_synced: 0,
			errors: [error.message || '同步失败']
		};
	}
}

// =============================================================================
// 文件上传到IM API
// =============================================================================

/**
 * 上传VNC Pod内的图片文件到IM系统
 * @param {string} podName - Pod名称（如 vnc-1）
 * @param {string} filePath - Pod内文件路径（如 /root/workspace/image.jpg）
 * @returns {Promise} IM上传结果 {code, message, data: {originUrl, thumbUrl}}
 */
export function uploadImageToIM(podName, filePath) {
	return vncHttp({
		url: `/api/v1/pods/${podName}/files/im-upload-image`,
		method: 'POST',
		params: { file_path: filePath }
	})
}

/**
 * 上传VNC Pod内的普通文件到IM系统
 * @param {string} podName - Pod名称（如 vnc-1）
 * @param {string} filePath - Pod内文件路径（如 /root/workspace/document.pdf）
 * @returns {Promise} IM上传结果 {code, message, data: "url_string"}
 */
export function uploadFileToIM(podName, filePath) {
	return vncHttp({
		url: `/api/v1/pods/${podName}/files/im-upload-file`,
		method: 'POST',
		params: { file_path: filePath }
	})
}

// =============================================================================
// 导出所有API（更新）
// =============================================================================

export default {
	// Pod管理
	listPods,
	getPodDetail,
	createPod,
	restartPod,
	stopPod,
	deletePod,
	checkPodStatus,
	ensurePodReady,

	// 聊天同步
	getUserChats,
	syncUserChats,
	getUserChatsStatus,
	autoSyncUserChats,

	// 文件上传到IM
	uploadImageToIM,
	uploadFileToIM
}
